import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatContainer } from '../components/ChatContainer';
import { ConversationListWithFolders } from '../components/ConversationListWithFolders';
import { ModelDropdown } from '../components/ModelDropdown';
import { ComparisonModelSelector } from '../components/ComparisonModelSelector';
import { ComparisonView } from '../components/ComparisonView';
import { UserProfileMenu } from '../components/UserProfileMenu';
import { FileLibrary } from '../components/FileLibrary';
import { apiClient } from '../services/api';
import { wsClient } from '../services/websocket';
import type { Model, ConversationListItem, Message } from '../types';

const ChatPage: React.FC = () => {
  const { conversationId: urlConversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();

  const [currentModelId, setCurrentModelId] = useState('gpt-4');
  const [conversationId, setConversationId] = useState<string | null>(urlConversationId || null);

  // Keep conversationId in sync with the URL (e.g. after navigate() updates params)
  useEffect(() => {
    setConversationId(urlConversationId || null);
  }, [urlConversationId]);
  const [initialMessages, setInitialMessages] = useState<Message[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [comparisonModelIds, setComparisonModelIds] = useState<[string, string]>(['', '']);
  const [showFileLibrary, setShowFileLibrary] = useState(false);

  // Fetch models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const result = await apiClient.getModels();
        if (result && result.length > 0) {
          setModels(result);
          setCurrentModelId(result[0].id);
        } else {
          // Fallback models if API returns empty
          setModels([
            {
              id: 'gpt-4',
              name: 'GPT-4',
              provider: 'OpenAI',
              contextWindow: 8192,
              costPerToken: { inputCostPer1M: 2.50, outputCostPer1M: 10.00 },
              available: true,
            },
          ]);
        }
      } catch {
        // Use fallback models on error
        setModels([
          {
            id: 'gpt-4',
            name: 'GPT-4',
            provider: 'OpenAI',
            contextWindow: 8192,
            costPerToken: { inputCostPer1M: 2.50, outputCostPer1M: 10.00 },
            available: true,
          },
        ]);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  // Fetch conversation details (with messages) when conversationId changes
  useEffect(() => {
    if (!conversationId) {
      setInitialMessages([]);
      return;
    }

    const fetchConversationDetails = async () => {
      try {
        const conversation = await apiClient.getConversation(conversationId);
        if (conversation && (conversation as any).messages) {
          setInitialMessages((conversation as any).messages);
          // Also update the current model if the conversation has a currentModelId
          if ((conversation as any).currentModelId) {
            setCurrentModelId((conversation as any).currentModelId);
          }
        } else {
          setInitialMessages([]);
        }
      } catch (err) {
        console.error('Failed to fetch conversation details:', err);
        setInitialMessages([]);
      }
    };

    fetchConversationDetails();
  }, [conversationId]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      const result = await apiClient.getConversations({ sortBy: 'lastUpdated' });
      const conversations = Array.isArray(result) ? result : [];
      // Normalize conversations to ensure all fields are present
      const normalized = conversations.map((conv: any) => ({
        id: conv.id || conv._id || '',
        title: conv.title || 'Untitled Conversation',
        lastMessage: conv.lastMessage || '',
        lastUpdated: conv.lastUpdated || conv.updatedAt || new Date().toISOString(),
        messageCount: conv.messageCount || 0,
        models: conv.models || [],
        folderId: conv.folderId,
      }));
      setConversations(normalized);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle new conversation
  const handleNewConversation = useCallback(async () => {
    try {
      setError(null);
      const result = await apiClient.createConversation(currentModelId);
      const conv = result;
      const newId = conv.id || (conv as any)._id;
      setConversationId(newId);
      navigate(`/chat/${newId}`, { replace: true });
      fetchConversations();
    } catch (err: any) {
      setError(err.error?.message || 'Failed to create conversation');
    }
  }, [currentModelId, navigate, fetchConversations]);

  // Handle selecting an existing conversation
  const handleSelectConversation = useCallback(async (id: string) => {
    setConversationId(id);
    navigate(`/chat/${id}`, { replace: true });
  }, [navigate]);

  // Handle deleting a conversation
  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await apiClient.deleteConversation(id);
      if (conversationId === id) {
        setConversationId(null);
        navigate('/', { replace: true });
      }
      fetchConversations();
    } catch (err: any) {
      setError(err.error?.message || 'Failed to delete conversation');
    }
  }, [conversationId, navigate, fetchConversations]);

  // Handle search
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      fetchConversations();
      return;
    }
    try {
      const result = await apiClient.searchConversations(query);
      const conversations = Array.isArray(result) ? result : [];
      // Normalize search results
      const normalized = conversations.map((conv: any) => ({
        id: conv.id || conv._id || '',
        title: conv.title || 'Untitled Conversation',
        lastMessage: conv.lastMessage || '',
        lastUpdated: conv.lastUpdated || conv.updatedAt || new Date().toISOString(),
        messageCount: conv.messageCount || 0,
        models: conv.models || [],
        folderId: conv.folderId,
      }));
      setConversations(normalized);
    } catch {
      // Keep current list on search error
      console.error('Search failed:', query);
    }
  }, [fetchConversations]);

  // Handle model switch
  const handleModelSwitch = useCallback((modelId: string) => {
    console.log('[ChatPage] Switching model to:', modelId);
    setCurrentModelId(modelId);

    // If there's an active conversation, notify the backend to update the conversation's currentModelId
    if (conversationId && wsClient.isConnected) {
      try {
        console.log('[ChatPage] Sending model switch to backend for:', conversationId);
        wsClient.switchModel(conversationId, modelId);
      } catch (err) {
        console.error('[ChatPage] Failed to notify backend of model switch:', err);
        // Still update local state even if WebSocket notification fails
      }
    } else if (conversationId && !wsClient.isConnected) {
      console.warn('[ChatPage] WebSocket not connected, cannot notify backend of model switch. Local state updated.');
    }
  }, [conversationId]);

  const handleToggleComparison = useCallback(() => {
    if (!isComparisonMode && models.length >= 2) {
      // Pick first two distinct available models
      const available = models.filter((m) => m.available);
      setComparisonModelIds([
        available[0]?.id || models[0].id,
        available[1]?.id || models[Math.min(1, models.length - 1)].id,
      ]);
    }
    setIsComparisonMode((prev) => !prev);
  }, [isComparisonMode, models]);

  const handleSelectComparisonModel = useCallback(
    (index: 0 | 1, modelId: string) => {
      setComparisonModelIds((prev) => {
        const updated = [...prev] as [string, string];
        updated[index] = modelId;
        return updated;
      });
    },
    []
  );

  // When selecting a conversation, exit comparison mode
  const handleSelectConversationWithExit = useCallback(
    (id: string) => {
      setIsComparisonMode(false);
      handleSelectConversation(id);
    },
    [handleSelectConversation]
  );

  const currentModel = models.find(m => m.id === currentModelId) || null;

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Top Header */}
      <header className="h-14 flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 z-20">
        <div className="flex items-center gap-3">
          {/* Sidebar toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white hidden sm:block">
              Unified LLM
            </h1>
          </button>

          {/* New conversation button — visible when sidebar is collapsed */}
          {!sidebarOpen && !isComparisonMode && (
            <button
              onClick={handleNewConversation}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
              aria-label="New conversation"
              title="New conversation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}

          {/* Compare toggle button */}
          <button
            onClick={handleToggleComparison}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5
              ${isComparisonMode
                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }
            `}
            title={isComparisonMode ? 'Exit comparison mode' : 'Compare two models side by side'}
            disabled={models.length < 2}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
            </svg>
            <span className="hidden sm:inline">
              {isComparisonMode ? 'Exit Compare' : 'Compare'}
            </span>
          </button>

          {/* Files library toggle button */}
          <button
            onClick={() => setShowFileLibrary((prev) => !prev)}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5
              ${showFileLibrary
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }
            `}
            title={showFileLibrary ? 'Close file library' : 'Open file library'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <span className="hidden sm:inline">Files</span>
          </button>
        </div>

        {/* Model selector(s) */}
        {isComparisonMode ? (
          <ComparisonModelSelector
            models={models}
            selectedModelIds={comparisonModelIds}
            onSelectModel={handleSelectComparisonModel}
            disabled={loadingModels}
          />
        ) : (
          <ModelDropdown
            models={models}
            currentModel={currentModel}
            onSelect={handleModelSwitch}
            disabled={loadingModels}
          />
        )}

        <UserProfileMenu />
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`
            ${sidebarOpen ? 'w-80' : 'w-0'}
            flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden
            border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800
            flex flex-col
          `}
        >
          <div className="flex flex-col h-full w-80">
            {/* Conversations section */}
            <div className="flex-1 overflow-hidden">
              <ConversationListWithFolders
                conversations={conversations}
                selectedConversationId={conversationId || undefined}
                onSelect={handleSelectConversationWithExit}
                onDelete={handleDeleteConversation}
                onNewConversation={handleNewConversation}
                onSearch={handleSearch}
                onConversationMoved={fetchConversations}
                isLoading={loadingConversations}
              />
            </div>

          </div>
        </aside>

        {/* Chat Area */}
        <main className="flex-1 flex flex-col overflow-hidden relative">
          {error && (
            <div className="mx-4 mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {isComparisonMode ? (
            <ComparisonView
              models={models}
              selectedModelIds={comparisonModelIds}
            />
          ) : conversationId ? (
            <ChatContainer
              conversationId={conversationId}
              initialMessages={initialMessages}
              currentModelId={currentModelId}
              models={models}
              onConversationUpdated={fetchConversations}
            />
          ) : (
            /* Welcome Screen */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="max-w-lg text-center space-y-6">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Welcome to Unified LLM
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    Chat with multiple AI models in one place. Switch between GPT, Claude, Gemini, and more.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-2">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Switch Models</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Change AI models mid-conversation
                    </p>
                  </div>

                  <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-2">
                      <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Real-time Streaming</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      See responses as they generate
                    </p>
                  </div>

                  <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-2">
                      <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Save & Organize</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Keep conversations in folders
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleNewConversation}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Start a Conversation
                </button>
              </div>
            </div>
          )}

          {/* File Library panel — slides in from the right side of the chat area */}
          {showFileLibrary && (
            <FileLibrary onClose={() => setShowFileLibrary(false)} />
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-400 dark:text-gray-500">
        <span>&copy; 2026 IH Software Services Provider &middot; All rights reserved.</span>
        <a
          href="https://ko-fi.com/imranhussain"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-gray-400 hover:text-[#FF5E5B] dark:hover:text-[#FF5E5B] transition-colors"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
          </svg>
          Buy me a coffee
        </a>
      </footer>
    </div>
  );
};

export default ChatPage;
